import { useEffect, useState } from "react"
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Typography,
  IconButton,
  Divider,
  Container,
  Grid,
  InputAdornment,
  Paper,
  Alert,
  CircularProgress,
  Menu,
} from "@mui/material"
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  MessageSquare,
  Plus,
  Bold,
  Italic,
  Strikethrough,
  Code,
  Save,
  Info,
  X,
  Link,
  Phone,
  MessageCircle,
  FileText,
  Copy,
  ChevronDown,
  Image as ImageIcon, Video as VideoIcon, FileText as FileIcon
} from "lucide-react";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { fetchAccountsFromAPI } from '../../Redux/AccountDucks';
import { readTemplateAPI, createTemplateAPI, updateTemplateAPI } from '../../Redux/TemplateDucks';
import WhatsAppPreview from "../../components/WhatsApp/WhatsAppPreview";
import { mapBackendToFrontend, mapFrontendToBackend } from "../../Utilities/TemplateMapper";

export default function WhatsAppTemplateDetailPage() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!templateId && templateId !== 'new';

  const [template, setTemplate] = useState({
    name: "",
    language: "es_CL",
    category: "MARKETING",
    header: "",
    headerFormat: "",
    content: "",
    footer: "",
    variables: [],
    parameterFormat: "POSITIONAL",
    buttons: [],
  })

  const [accounts, setAccounts] = useState([])
  const [selectedAccount, setSelectedAccount] = useState('0')
  const [selectedVariable, setSelectedVariable] = useState("POSITIONAL")
  const [bodyCharCount, setBodyCharCount] = useState(0)
  const [footerCharCount, setFooterCharCount] = useState(0)
  const [headerText, setHeaderText] = useState("")
  const [buttonMenuAnchor, setButtonMenuAnchor] = useState(null)
  const [variableExamples, setVariableExamples] = useState({
    nombre: "",
  })

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)


  const dispatch = useDispatch()
  const accountList = useSelector(store => store.account.accounts)
  const currentTemplate = useSelector(store => store.template.currentTemplate)

  useEffect(() => {
    dispatch(fetchAccountsFromAPI());
  }, [])

  useEffect(() => {
    if (accountList.length > 0) {
      setAccounts(accountList)
    }
  }, [accountList])

  useEffect(() => {
    if (isEditMode) {
      dispatch(readTemplateAPI(templateId));
    }
  }, [templateId, isEditMode, dispatch]);

  useEffect(() => {
    if (currentTemplate && isEditMode) {
      console.log('Template recibido del backend:', currentTemplate);

      const mappedTemplate = mapBackendToFrontend(currentTemplate);

      console.log('Template mapeado para el frontend:', mappedTemplate);

      if (mappedTemplate) {
        setSelectedAccount(currentTemplate.accountId?.toString() || '0');
        setTemplate(mappedTemplate);
        setVariableExamples(mappedTemplate.variableExamples); // 🔥 NUEVO
        setBodyCharCount(mappedTemplate.content?.length || 0);
        setFooterCharCount(mappedTemplate.footer?.length || 0);

        if (mappedTemplate.header === "text" && mappedTemplate.headerText) {
          setHeaderText(mappedTemplate.headerText);
        }
      }
    }
  }, [currentTemplate, isEditMode]);

  const handleChangeName = (event) => {
    var value = event.target.value
    if (value.length <= 512) {
      setTemplate((prev) => ({ ...prev, name: value }))
    }

  }

  const handleHeaderChange = (value) => {
    setTemplate((prev) => ({ ...prev, headerText: value }))
    setHeaderText(value)

  }

  const handleBackward = () => {
    window.history.back();
  }

  const handleBodyChange = (newContent) => {
    // Actualizar contador de caracteres
    setBodyCharCount(newContent.length);

    setTemplate((prev) => {
      const updatedTemplate = { ...prev, content: newContent };

      if (prev.parameterFormat === "NAMED") {
        // Extraer todas las variables del contenido
        const variablesInContent = [];
        const regex = /\{\{(\w+)\}\}/g;
        let match;

        while ((match = regex.exec(newContent)) !== null) {
          const varName = match[1];
          if (!variablesInContent.includes(varName)) {
            variablesInContent.push(varName);
          }
        }

        // Detectar variables eliminadas
        const removedVars = prev.variables.filter(v => !variablesInContent.includes(v));

        // Detectar variables nuevas o renombradas
        const addedVars = variablesInContent.filter(v => !prev.variables.includes(v));

        // Si hay cambios, actualizar variables y ejemplos
        if (removedVars.length > 0 || addedVars.length > 0) {
          // Actualizar ejemplos
          setVariableExamples((prevExamples) => {
            const newExamples = { ...prevExamples };

            // Si es un renombramiento (1 eliminada, 1 agregada), transferir el ejemplo
            if (removedVars.length === 1 && addedVars.length === 1) {
              const oldVar = removedVars[0];
              const newVar = addedVars[0];
              if (prevExamples[oldVar]) {
                newExamples[newVar] = prevExamples[oldVar];
              }
              delete newExamples[oldVar];
            } else {
              // Remover ejemplos de variables eliminadas
              removedVars.forEach(v => delete newExamples[v]);

              // Agregar ejemplos vacíos para variables nuevas
              addedVars.forEach(v => {
                if (!newExamples[v]) {
                  newExamples[v] = "";
                }
              });
            }

            return newExamples;
          });

          updatedTemplate.variables = variablesInContent;
        }
      } else {
        // Para POSITIONAL, mantener la lógica existente
        const variablesInContent = [];
        const regex = /\{\{(\d+)\}\}/g;
        let match;

        while ((match = regex.exec(newContent)) !== null) {
          const varName = match[1];
          if (!variablesInContent.includes(varName)) {
            variablesInContent.push(varName);
          }
        }

        // Ordenar numéricamente
        variablesInContent.sort((a, b) => parseInt(a) - parseInt(b));

        if (JSON.stringify(variablesInContent) !== JSON.stringify(prev.variables)) {
          updatedTemplate.variables = variablesInContent;

          // Limpiar ejemplos de variables que ya no existen
          setVariableExamples((prevExamples) => {
            const newExamples = { ...prevExamples };
            Object.keys(newExamples).forEach(key => {
              if (!variablesInContent.includes(key)) {
                delete newExamples[key];
              }
            });
            return newExamples;
          });
        }
      }

      return updatedTemplate;
    });
  };

  const handleFooterChange = (value) => {
    setTemplate((prev) => ({ ...prev, footer: value }))
    setFooterCharCount(value.length)
  }

  const addVariable = () => {
    const textarea = document.querySelector('textarea');
    if (!textarea) return;

    const cursorPosition = textarea.selectionStart || template.content.length;
    const textBefore = template.content.substring(0, cursorPosition);
    const textAfter = template.content.substring(cursorPosition);

    let variableName;

    if (template.parameterFormat === "POSITIONAL") {
      // Para numéricas: {{1}}, {{2}}, {{3}}...
      const nextNumber = template.variables.length + 1;
      variableName = nextNumber.toString();
    } else {
      // Para nombradas: {{VARIABLE_1}}, {{VARIABLE_2}}...
      //variableName = `VARIABLE_${template.variables.length + 1}`;
      variableName = '';
    }

    const variableText = `{{${variableName}}}`;
    const newContent = textBefore + variableText + textAfter;

    // Actualizar contenido (handleBodyChange se encargará del resto)
    handleBodyChange(newContent);

    // Restaurar foco y posición del cursor
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        cursorPosition + variableText.length,
        cursorPosition + variableText.length
      );
    }, 0);
  };

  const handleAddButton = (type) => {
    if (template.buttons.length >= 10) {
      return
    }

    const newButton = {
      id: Date.now(),
      type: type,
      text: "",
    }

    if (type === "URL") {
      newButton.url = ""
    } else if (type === "PHONE_NUMBER") {
      newButton.phone_number = ""
    }

    setTemplate((prev) => ({
      ...prev,
      buttons: [...prev.buttons, newButton],
    }))
    setButtonMenuAnchor(null)
  }

  const handleRemoveButton = (buttonId) => {
    setTemplate((prev) => ({
      ...prev,
      buttons: prev.buttons.filter((btn) => btn.id !== buttonId),
    }))
  }

  const handleButtonChange = (buttonId, field, value) => {
    setTemplate((prev) => ({
      ...prev,
      buttons: prev.buttons.map((btn) => (btn.id === buttonId ? { ...btn, [field]: value } : btn)),
    }))
  }

  const getButtonTypeLabel = (type) => {
    const labels = {
      QUICK_REPLY: "Respuesta rápida",
      URL: "Ir al sitio web",
      PHONE_NUMBER: "Llamar al número de teléfono",
      WHATSAPP: "Llamar en WhatsApp",
      FLOW: "Completar proceso",
      COPY_CODE: "Copiar código de oferta",
    }
    return labels[type] || "Personalizado"
  }

  const getButtonIcon = (type) => {
    const icons = {
      QUICK_REPLY: MessageCircle,
      URL: Link,
      PHONE_NUMBER: Phone,
      WHATSAPP: MessageCircle,
      FLOW: FileText,
      COPY_CODE: Copy,
    }
    const Icon = icons[type] || MessageCircle
    return <Icon style={{ width: 16, height: 16 }} />
  }


  const buildTemplateComponents = () => {
    return mapFrontendToBackend(template);
  };

  const handleSelectAccount = (accountId) => {
    setSelectedAccount(accountId)
  }

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Validaciones
      if (!template.name) {
        setError("El nombre de la plantilla es requerido");
        setLoading(false);
        return;
      }

      if (!template.content) {
        setError("El contenido de la plantilla es requerido");
        setLoading(false);
        return;
      }

      if (!selectedAccount || selectedAccount === '0') {
        setError("No se ha seleccionado una cuenta");
        setLoading(false);
        return;
      }

      const missingExamples = template.variables.filter(v => !variableExamples[v]);
      if (missingExamples.length > 0) {
        setError(`Faltan ejemplos para las variables: ${missingExamples.join(', ')}`);
        setLoading(false);
        return;
      }

      if (isEditMode) {
        // Preparar datos completos del template para edición
        const templateData = mapFrontendToBackend({
          ...template,
          headerText: headerText
        }, variableExamples);

        const templateId = currentTemplate?.id || template?.id;

        if (!templateId) {
          setError("No se encontró el ID del template");
          setLoading(false);
          return;
        }

        // Si el template está APPROVED, no incluir category en la actualización
        const updateData = {
          updateData: {
            components: templateData.components, // Editar componentes
            // NO incluir category si está APPROVED
            ...(currentTemplate?.status !== "APPROVED" && { category: template.category })
          }
        };

        console.log('Datos de actualización:', updateData);

        await dispatch(updateTemplateAPI(templateId, updateData));
        setSuccess(true);

        setTimeout(() => {
          navigate('/campaigns/templates');
        }, 2000);
      } else {
        // Modo creación
        const templateData = mapFrontendToBackend({
          ...template,
          headerText: headerText
        }, variableExamples);

        const createData = {
          accountId: selectedAccount,
          templateData: templateData
        };

        console.log('Datos a enviar:', createData);
        console.log('Variable examples:', variableExamples);

        await dispatch(createTemplateAPI(createData));
        setSuccess(true);

        setTimeout(() => {
          navigate('/campaigns/templates'); // 🔥 Ruta corregida
        }, 2000);
      }
    } catch (err) {
      console.error('Error saving template:', err);
      setError(err.message || "Error al guardar la plantilla");
    } finally {
      setLoading(false);
    }
  };

  const handleVariableExampleChange = (variable, value) => {
    setVariableExamples((prev) => ({
      ...prev,
      [variable]: value,
    }))
  }

  const handleRemoveVariable = (variable) => {
    // Remover del contenido
    const regex = new RegExp(`\\{\\{${variable}\\}\\}`, 'g');
    const newContent = template.content.replace(regex, '');

    handleBodyChange(newContent);

    // Remover del array de variables
    setTemplate((prev) => ({
      ...prev,
      variables: prev.variables.filter(v => v !== variable),
    }));

    // Remover del objeto de ejemplos
    setVariableExamples((prev) => {
      const newExamples = { ...prev };
      delete newExamples[variable];
      return newExamples;
    });
  };


  /* funciones de editor de body */

  // Función auxiliar para aplicar formato al texto
  const applyTextFormat = (formatStart, formatEnd = formatStart) => {
    const textarea = document.querySelector('textarea');
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentText = template.content;

    let newText;
    let newCursorPos;

    if (start !== end) {
      // Hay texto seleccionado
      const selectedText = currentText.substring(start, end);
      newText =
        currentText.substring(0, start) +
        formatStart + selectedText + formatEnd +
        currentText.substring(end);
      newCursorPos = end + formatStart.length + formatEnd.length;
    } else {
      // No hay texto seleccionado
      newText =
        currentText.substring(0, start) +
        formatStart + formatEnd +
        currentText.substring(start);
      newCursorPos = start + formatStart.length;
    }

    handleBodyChange(newText);

    // Restaurar el foco y la posición del cursor
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  // Funciones para cada botón
  const handleBold = () => {
    applyTextFormat('*');
  };

  const handleItalic = () => {
    applyTextFormat('_');
  };

  const handleStrikethrough = () => {
    applyTextFormat('~');
  };

  const handleMonospace = () => {
    applyTextFormat('```');
  };


  return (
    <Container maxWidth={false} sx={{ px: 2 }}>
      <Button variant="text" startIcon={< ArrowBackIcon />} onClick={handleBackward} >Volver</Button>
      <Card sx={{ display: "flex", height: "86vh", bgcolor: "white" }} elevation={1}>
        <CardContent sx={{ p: 0, flex: '1 1 auto' }}>
          <Box sx={{ px: 2, height: "100%", maxWidth: "100%" }}>
            {/* Header con botón guardar */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 2, borderBottom: 1, borderColor: "divider" }}>
              <Typography variant="h5" fontWeight="bold">
                {isEditMode ? 'Editar Plantilla' : 'Crear Plantilla'}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 400 }}>
                <FormControl size="small">
                  <Select
                    value={selectedAccount}
                    onChange={(e) => handleSelectAccount(e.target.value)}
                    sx={{
                      bgcolor: "white",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#e0e0e0",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#1a1a1a",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#1a1a1a",
                      },
                    }}
                    disabled={isEditMode}
                  >
                    <MenuItem key='0' value='0'>
                      Seleccione una cuenta
                    </MenuItem>
                    {accounts.map((account) => (
                      <MenuItem key={account.id} value={account.id}>
                        {account.number}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save style={{ width: 20, height: 20 }} />}
                  onClick={handleSave}
                  disabled={isEditMode || loading || selectedAccount === '0'}
                >
                  {loading ? 'Guardando...' : 'Guardar Plantilla'}
                </Button>
              </Box>

            </Box>

            {/* Alertas */}
            {error && (
              <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccess(false)}>
                Plantilla guardada exitosamente. Redirigiendo...
              </Alert>
            )}

            <Grid container spacing={4} sx={{ height: "calc(100% - 80px)" }}>
              {/* Form Section */}
              <Grid item xs={12} lg={8} sx={{ display: "flex", flexDirection: "column", height: "100%", mt: 4 }}>
                <Card sx={{ display: "flex", flexDirection: "column", height: "100%" }} elevation={0}>
                  <CardContent sx={{ p: 3, overflow: 'auto', flex: '1 1 auto' }}>
                    {/* Template Name and Language */}
                    <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      Nombre e idioma de la plantilla
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Asigna un nombre a la plantilla
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Nombre de la plantilla"
                          value={template.name}
                          onChange={handleChangeName}
                          placeholder="Ej: agenda_confirmacion"
                          disabled={isEditMode}
                          error={template.name.includes(" ")}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Typography variant="caption" color="text.secondary">
                                  {template.name.length}/512
                                </Typography>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                          <InputLabel>Selecciona el idioma</InputLabel>
                          <Select
                            value={template.language}
                            label="Selecciona el idioma"
                            onChange={(e) => setTemplate((prev) => ({ ...prev, language: e.target.value }))}
                            disabled={isEditMode}
                          >
                            <MenuItem value="es_CL">Spanish (CHL)</MenuItem>
                            <MenuItem value="es">Spanish Generico</MenuItem>
                            <MenuItem value="en_US">English (US)</MenuItem>
                            <MenuItem value="pt_BR">Portuguese (BR)</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                          <InputLabel>Categoría</InputLabel>
                          <Select
                            value={template.category}
                            label="Categoría"
                            onChange={(e) => setTemplate((prev) => ({ ...prev, category: e.target.value }))}
                            disabled={isEditMode && currentTemplate?.status == "APPROVED" }
                          >
                            <MenuItem value="MARKETING">Marketing</MenuItem>
                            <MenuItem value="UTILITY">Utilidad</MenuItem>
                            <MenuItem value="AUTHENTICATION">Autenticación</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    {/* Content Section */}
                    <Typography variant="h6" gutterBottom>
                      Contenido
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Rellena las secciones de encabezado, cuerpo y pie de página de tu plantilla.
                    </Typography>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                      {/* Variable Selection */}
                      <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            Tipo de Variable
                          </Typography>

                        </Box>
                        <FormControl fullWidth>
                          <Select
                            value={template.parameterFormat}
                            onChange={(e) => {
                              setTemplate(prev => ({
                                ...prev,
                                parameterFormat: e.target.value
                              }));
                            }}
                            disabled={template.variables.length > 0 || isEditMode}
                          >
                            <MenuItem value="NAMED">Nombre</MenuItem>
                            <MenuItem value="POSITIONAL">Número</MenuItem>
                          </Select>

                          {/* Mensaje explicativo */}
                          {template.variables.length > 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                              No puedes cambiar el tipo de variable mientras haya variables en uso
                            </Typography>
                          )}
                        </FormControl>
                      </Box>

                      {/* Header */}
                      <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            Encabezado
                          </Typography>
                          <Chip label="Opcional" size="small" variant="outlined" />
                        </Box>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                          <Select
                            value={template.header || ""}
                            displayEmpty
                            disabled={isEditMode}
                            onChange={(e) => {
                              const value = e.target.value;
                              setTemplate((prev) => ({
                                ...prev,
                                header: value,
                                headerFormat: value ? value.toUpperCase() : "",
                                // Limpiar headerText si no es tipo texto
                                headerText: value === "text" ? prev.headerText : ""
                              }));

                              // Si se selecciona "Ninguno", limpiar también headerText local
                              if (!value) {
                                setHeaderText("");
                              }
                            }}
                          >
                            <MenuItem value="">Ninguno</MenuItem>
                            <MenuItem value="text">Texto</MenuItem>
                            <MenuItem value="image">Imagen</MenuItem>
                            <MenuItem value="video">Video</MenuItem>
                            <MenuItem value="document">Documento</MenuItem>
                          </Select>
                        </FormControl>

                        {/* Texto del encabezado */}
                        {template.header === "text" && (
                          <TextField
                            fullWidth
                            label="Texto del encabezado"
                            value={headerText}
                            onChange={(e) => handleHeaderChange(e.target.value)}
                            placeholder="Ingresa el texto del encabezado"
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Typography variant="caption" color="text.secondary">
                                    {headerText.length}/60
                                  </Typography>
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}

                        {/* Upload para imagen */}
                        {template.header === "image" && (
                          <Paper
                            elevation={0}
                            sx={{
                              p: 3,
                              border: "2px dashed rgba(0,0,0,0.12)",
                              borderRadius: 2,
                              textAlign: "center",
                              cursor: "pointer",
                              "&:hover": {
                                borderColor: "primary.main",
                                bgcolor: "rgba(0,0,0,0.02)",
                              },
                            }}
                          >
                            <ImageIcon style={{ width: 48, height: 48, opacity: 0.5, margin: "0 auto 8px" }} />
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Arrastra y suelta para subir el archivo
                            </Typography>
                            <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                              O elige archivos de tu dispositivo
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                              Formatos admitidos: JPG, PNG. Tamaño máximo: 5MB
                            </Typography>
                          </Paper>
                        )}

                        {/* Upload para video */}
                        {template.header === "video" && (
                          <Paper
                            elevation={0}
                            sx={{
                              p: 3,
                              border: "2px dashed rgba(0,0,0,0.12)",
                              borderRadius: 2,
                              textAlign: "center",
                              cursor: "pointer",
                              "&:hover": {
                                borderColor: "primary.main",
                                bgcolor: "rgba(0,0,0,0.02)",
                              },
                            }}
                          >
                            <VideoIcon style={{ width: 48, height: 48, opacity: 0.5, margin: "0 auto 8px" }} />
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Arrastra y suelta para subir el archivo
                            </Typography>
                            <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                              O elige archivos de tu dispositivo
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                              Formatos admitidos: MP4. Tamaño máximo: 16MB
                            </Typography>
                          </Paper>
                        )}

                        {/* Upload para documento */}
                        {template.header === "document" && (
                          <Paper
                            elevation={0}
                            sx={{
                              p: 3,
                              border: "2px dashed rgba(0,0,0,0.12)",
                              borderRadius: 2,
                              textAlign: "center",
                              cursor: "pointer",
                              "&:hover": {
                                borderColor: "primary.main",
                                bgcolor: "rgba(0,0,0,0.02)",
                              },
                            }}
                          >
                            <FileIcon style={{ width: 48, height: 48, opacity: 0.5, margin: "0 auto 8px" }} />
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Arrastra y suelta para subir el archivo
                            </Typography>
                            <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
                              O elige archivos de tu dispositivo
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                              Formatos admitidos: PDF. Tamaño máximo: 100MB
                            </Typography>
                          </Paper>
                        )}
                      </Box>

                      {/* Body Text */}
                      <Box>
                        <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                          Texto
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={4}
                          value={template.content}
                          disabled={isEditMode}
                          onChange={(e) => handleBodyChange(e.target.value)}
                          placeholder="Hola ..."
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end" sx={{ alignSelf: "flex-end", mb: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  {bodyCharCount}/1024
                                </Typography>
                              </InputAdornment>
                            ),
                          }}
                        />

                        {/* Formatting Toolbar */}
                        <Paper
                          elevation={0}
                          sx={{
                            mt: 1,
                            p: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            bgcolor: "rgba(0,0,0,0.03)",
                            border: "1px solid rgba(0,0,0,0.12)",
                          }}
                        >
                          <IconButton size="small" onClick={handleBold}>
                            <Bold style={{ width: 16, height: 16 }} />
                          </IconButton>
                          <IconButton size="small" onClick={handleItalic}>
                            <Italic style={{ width: 16, height: 16 }} />
                          </IconButton>
                          <IconButton size="small" onClick={handleStrikethrough}>
                            <Strikethrough style={{ width: 16, height: 16 }} />
                          </IconButton>
                          <IconButton size="small" onClick={handleMonospace}>
                            <Code style={{ width: 16, height: 16 }} />
                          </IconButton>
                          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                          <Button size="small" startIcon={<Plus style={{ width: 16, height: 16 }} />} onClick={addVariable}>
                            Añadir variable
                          </Button>
                        </Paper>
                      </Box>

                      {/* Body Content Samples */}
                      {template.variables.length > 0 && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          <Typography variant="body2" fontWeight="medium">
                            Texto
                          </Typography>
                          {template.variables.map((variable) => (
                            <Box key={variable}>
                              <Grid container spacing={2} alignItems="flex-start">
                                <Grid item xs={3}>
                                  <Paper
                                    elevation={0}
                                    sx={{
                                      p: 1.5,
                                      bgcolor: "rgba(0,0,0,0.03)",
                                      border: "1px solid rgba(0,0,0,0.12)",
                                      borderRadius: 1,
                                      height: "56px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                                      {`{{${variable}}}`}
                                    </Typography>
                                  </Paper>
                                </Grid>
                                <Grid item xs={9}>
                                  <Box sx={{ position: "relative" }}>
                                    <TextField
                                      fullWidth
                                      size="medium"
                                      value={variableExamples[variable] || ""}
                                      disabled={isEditMode}
                                      onChange={(e) => handleVariableExampleChange(variable, e.target.value)}
                                      placeholder={`Introduce contenido para {{${variable}}}`}
                                      error={!variableExamples[variable]}
                                      InputProps={{
                                        endAdornment: (
                                          <InputAdornment position="end">
                                            <IconButton
                                              size="small"
                                              onClick={() => handleRemoveVariable(variable)}
                                              edge="end"
                                            >
                                              <X style={{ width: 16, height: 16 }} />
                                            </IconButton>
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                    {!variableExamples[variable] && (
                                      <Typography
                                        variant="caption"
                                        color="error"
                                        sx={{ display: "block", mt: 0.5, ml: 1.5 }}
                                      >
                                        Añade texto de ejemplo
                                      </Typography>
                                    )}
                                  </Box>
                                </Grid>
                              </Grid>
                            </Box>
                          ))}
                        </Box>
                      )}

                      {/* Footer */}
                      <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            Pie de página
                          </Typography>
                          <Chip label="Opcional" size="small" variant="outlined" />
                        </Box>
                        <TextField
                          fullWidth
                          value={template.footer}
                          disabled={isEditMode}
                          onChange={(e) => handleFooterChange(e.target.value)}
                          placeholder="Introduce texto"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Typography variant="caption" color="text.secondary">
                                  {footerCharCount}/60
                                </Typography>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>

                      <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            Botones
                          </Typography>
                          <Chip label="Opcional" size="small" variant="outlined" />
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Crea botones para que los clientes puedan responder tu mensaje o realizar una acción. Puedes
                          añadir un máximo de 10 botones. Si añades más de 3 botones, aparecerán en forma de lista.
                        </Typography>

                        {/* Add Button Dropdown */}
                        <Button
                          variant="outlined"
                          startIcon={<Plus style={{ width: 16, height: 16 }} />}
                          endIcon={<ChevronDown style={{ width: 16, height: 16 }} />}
                          onClick={(e) => setButtonMenuAnchor(e.currentTarget)}
                          disabled={template.buttons.length >= 10 || isEditMode}
                          sx={{ mb: 2 }}
                        >
                          Añadir botón
                        </Button>

                        <Menu
                          anchorEl={buttonMenuAnchor}
                          open={Boolean(buttonMenuAnchor)}
                          onClose={() => setButtonMenuAnchor(null)}
                        >
                          <MenuItem onClick={() => handleAddButton("QUICK_REPLY")}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <MessageCircle style={{ width: 16, height: 16 }} />
                              Personalizado
                            </Box>
                          </MenuItem>
                          <MenuItem onClick={() => handleAddButton("URL")}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Link style={{ width: 16, height: 16 }} />
                              Ir al sitio web
                            </Box>
                          </MenuItem>
                          <MenuItem onClick={() => handleAddButton("WHATSAPP")}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <MessageCircle style={{ width: 16, height: 16 }} />
                              Llamar en WhatsApp
                            </Box>
                          </MenuItem>
                          <MenuItem onClick={() => handleAddButton("PHONE_NUMBER")}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Phone style={{ width: 16, height: 16 }} />
                              Llamar al número de teléfono
                            </Box>
                          </MenuItem>
                          <MenuItem onClick={() => handleAddButton("FLOW")}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <FileText style={{ width: 16, height: 16 }} />
                              Completar proceso
                            </Box>
                          </MenuItem>
                          <MenuItem onClick={() => handleAddButton("COPY_CODE")}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Copy style={{ width: 16, height: 16 }} />
                              Copiar código de oferta
                            </Box>
                          </MenuItem>
                        </Menu>

                        {/* Button List */}
                        {template.buttons.length > 0 && (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            {template.buttons.map((button, index) => (
                              <Paper
                                key={button.id}
                                elevation={0}
                                sx={{
                                  p: 2,
                                  border: "1px solid rgba(0,0,0,0.12)",
                                  borderRadius: 1,
                                }}
                              >
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                                  <Box sx={{ flex: 1 }}>
                                    <Grid container spacing={2}>
                                      <Grid item xs={12} md={6}>
                                        <FormControl fullWidth size="small">
                                          <InputLabel>Type</InputLabel>
                                          <Select
                                            value={button.type}
                                            label="Type"
                                            disabled={isEditMode}
                                            onChange={(e) => handleButtonChange(button.id, "type", e.target.value)}
                                            startAdornment={
                                              <InputAdornment position="start">{getButtonIcon(button.type)}</InputAdornment>
                                            }
                                          >
                                            <MenuItem value="QUICK_REPLY">Personalizado</MenuItem>
                                            <MenuItem value="URL">Ir al sitio web</MenuItem>
                                            <MenuItem value="WHATSAPP">Llamar en WhatsApp</MenuItem>
                                            <MenuItem value="PHONE_NUMBER">Llamar al número de teléfono</MenuItem>
                                            <MenuItem value="FLOW">Completar proceso</MenuItem>
                                            <MenuItem value="COPY_CODE">Copiar código de oferta</MenuItem>
                                          </Select>
                                        </FormControl>
                                      </Grid>
                                      <Grid item xs={12} md={6}>
                                        <TextField
                                          fullWidth
                                          size="small"
                                          label="Texto del botón"
                                          value={button.text}
                                          disabled={isEditMode}
                                          onChange={(e) => handleButtonChange(button.id, "text", e.target.value)}
                                          placeholder="Quick Reply"
                                          inputProps={{ maxLength: 25 }}
                                          InputProps={{
                                            endAdornment: (
                                              <InputAdornment position="end">
                                                <Typography variant="caption" color="text.secondary">
                                                  {button.text.length}/25
                                                </Typography>
                                              </InputAdornment>
                                            ),
                                          }}
                                        />
                                      </Grid>
                                      {button.type === "PHONE_NUMBER" && (
                                        <Grid item xs={12}>
                                          <TextField
                                            fullWidth
                                            size="small"
                                            label="Número de teléfono"
                                            value={button.phone_number || ""}
                                            disabled={isEditMode}
                                            onChange={(e) => handleButtonChange(button.id, "phone_number", e.target.value)}
                                            placeholder="+1234567890"
                                          />
                                        </Grid>
                                      )}
                                    </Grid>
                                  </Box>
                                  <IconButton size="small" onClick={() => handleRemoveButton(button.id)} sx={{ mt: 0.5 }}>
                                    <X style={{ width: 16, height: 16 }} />
                                  </IconButton>
                                </Box>
                              </Paper>
                            ))}
                          </Box>
                        )}

                        {/* Quick Reply Section */}
                        {template.buttons.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              Respuesta rápida • Opcional
                            </Typography>
                          </Box>
                        )}
                      </Box>

                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Preview Section */}
              <Grid item xs={12} lg={4}>
                <WhatsAppPreview template={template} variableExamples={variableExamples} />
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </Container>
  )
}